"use client";

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import AuthGuardModal from './AuthGuardModal';

export default function Navbar() {
    const pathname = usePathname();
    const { theme, toggleTheme } = useTheme();
    const { user, logout } = useAuth(); // Auth context
    const [scrolled, setScrolled] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setShowUserMenu(false);
        if (showUserMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showUserMenu]);

    const handleThemeToggle = () => {
        setIsAnimating(true);
        toggleTheme();
        setTimeout(() => setIsAnimating(false), 400);
    };

    const links = [
        { href: '/', label: 'Home', icon: '🏠', hideOnMobile: true }, // Hide on mobile since logo goes to home
        { href: '/dashboard', label: 'Dashboard', icon: '📊' },
        { href: '/editor', label: 'Editor', icon: '✏️' },
    ];

    return (
        <>
            <nav
                className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${scrolled ? 'w-[90%] max-w-2xl' : 'w-[95%] max-w-4xl'
                    }`}
            >
                <div className={`
            relative px-4 md:px-6 py-2 md:py-3 rounded-full 
            backdrop-blur-xl border
            shadow-lg 
            flex items-center justify-between
            transition-all duration-300
            ${theme === 'dark'
                        ? 'bg-white/5 border-white/10 shadow-black/20'
                        : 'bg-white/80 border-slate-200 shadow-slate-300/30'
                    }
            ${scrolled
                        ? theme === 'dark'
                            ? 'bg-black/40 border-white/20'
                            : 'bg-white/90 border-slate-300'
                        : ''
                    }
          `}>
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2 group">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-sm group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                            ✨
                        </div>
                        <span className={`font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600 hidden sm:block`}>
                            PRISM
                        </span>
                    </Link>

                    {/* Links */}
                    <div className="flex items-center gap-1 sm:gap-2">
                        {links.map((link) => {
                            const isActive = pathname === link.href;
                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className={`
                      relative px-3 md:px-4 py-2 rounded-full text-sm font-medium transition-all duration-300
                      flex items-center gap-1.5 md:gap-2
                      ${link.hideOnMobile ? 'hidden md:flex' : ''}
                      ${isActive
                                            ? theme === 'dark'
                                                ? 'text-white bg-white/10 shadow-inner'
                                                : 'text-slate-900 bg-slate-100 shadow-inner'
                                            : theme === 'dark'
                                                ? 'text-slate-400 hover:text-white hover:bg-white/5'
                                                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                                        }
                    `}
                                >
                                    <span>{link.icon}</span>
                                    <span className="block">
                                        {link.label}
                                    </span>
                                    {isActive && (
                                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(192,132,252,0.8)]"></span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-1 md:gap-2">
                        {/* Theme Toggle */}
                        <button
                            onClick={handleThemeToggle}
                            className={`
                            w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-base md:text-lg
                            transition-all duration-300 cursor-pointer
                            ${theme === 'dark'
                                    ? 'bg-slate-800/50 border border-white/10 hover:border-yellow-400/50 hover:bg-yellow-500/10'
                                    : 'bg-slate-100 border border-slate-200 hover:border-purple-400/50 hover:bg-purple-50'
                                }
                            ${isAnimating ? 'theme-switch-animate' : ''}
                        `}
                            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {theme === 'dark' ? '☀️' : '🌙'}
                        </button>

                        {/* User Profile (Only if logged in) */}
                        {user && (
                            <div className="relative ml-1 flex items-center gap-2 pl-2 border-l border-gray-200 dark:border-gray-700">
                                {/* Avatar - Clickable on all devices */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
                                    className="focus:outline-none"
                                >
                                    {user.photoURL ? (
                                        <img
                                            src={user.photoURL}
                                            alt={user.displayName || "User"}
                                            className="w-8 h-8 rounded-full border-2 border-transparent hover:border-purple-500 transition-colors cursor-pointer"
                                            title={user.displayName || "User"}
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs cursor-pointer hover:bg-purple-600 transition-colors">
                                            {user.displayName ? user.displayName[0] : "U"}
                                        </div>
                                    )}
                                </button>

                                {/* Desktop: Separate logout button */}
                                <button
                                    onClick={() => logout()}
                                    className="hidden md:block text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                >
                                    Logout
                                </button>

                                {/* User Menu Dropdown (for avatar click) */}
                                {showUserMenu && (
                                    <div className={`absolute right-0 top-full mt-2 w-40 rounded-xl shadow-xl overflow-hidden z-50 ${theme === 'dark' ? 'bg-slate-800 border border-white/10' : 'bg-white border border-slate-200'
                                        }`}>
                                        <div className={`px-3 py-2 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-100'}`}>
                                            <p className={`text-xs truncate ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {user.displayName || user.email}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => { logout(); setShowUserMenu(false); }}
                                            className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${theme === 'dark' ? 'hover:bg-white/10 text-red-400' : 'hover:bg-slate-50 text-red-600'
                                                }`}
                                        >
                                            🚪 Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </nav>
        </>
    );
}

